"use client";

import { useState, useEffect, useRef, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { FormState, TreeConfig } from "../types";
import { INITIAL_FORM_STATE } from "../constants";
import { UseForm2Props, VariantConfig } from "./types";

export const useFormState = ({ selectedSection, variantDetails }: UseForm2Props & { variantDetails?: any }) => {
  const [formState, setFormState] = useState<FormState>({
    ...INITIAL_FORM_STATE,
    expandedIds: new Set(),
  });
  const previousSectionId = useRef<string | null>(null);

  const dispatch = useAppDispatch();
  const currentVariantDetails = variantDetails as VariantConfig | null; // Use prop instead of importing
  const currentUser = useAppSelector((state) => state.user?.user);

  const reduxState = useAppSelector((state) =>
    currentVariantDetails?.selector
      ? currentVariantDetails.selector(state.page as any)
      : null
  );

  // Memoized config values
  const componentFieldsConfig = useMemo(
    () => currentVariantDetails?.componentFields || [],
    [currentVariantDetails?.componentFields]
  );

  const parentComponentFieldsConfig = useMemo(
    () => currentVariantDetails?.parentComponentFields || [],
    [currentVariantDetails?.parentComponentFields]
  );

  const treeConfig: TreeConfig | undefined = currentVariantDetails?.treeConfig;

  // API methods from config
  const fetchMethod = currentVariantDetails?.fetchMethod;
  const deleteMethod = currentVariantDetails?.deleteMethod;
  const parentComponentGetMethod = currentVariantDetails?.parentComponentGetMethod;
  const parentComponentCreateMethod = currentVariantDetails?.parentComponentCreateMethod;
  const parentComponentUpdateMethod = currentVariantDetails?.parentComponentUpdateMethod;

  // Memoized fields config with parent dropdown options
  const fieldsConfig = useMemo(() => {
    if (!treeConfig?.parentFieldName) {
      return componentFieldsConfig;
    }

    // Always inject options into the parent field, even if empty
    // This ensures the dropdown is properly configured when options arrive
    return componentFieldsConfig.map((field: any) =>
      field.fieldName === treeConfig.parentFieldName
        ? { ...field, options: formState.parentDropdownOptions }
        : field
    );
  }, [componentFieldsConfig, formState.parentDropdownOptions, treeConfig?.parentFieldName]);

  // Reset state when switching widgets
  useEffect(() => {
    const currentSectionId = selectedSection?.id;

    if (previousSectionId.current !== null && previousSectionId.current !== currentSectionId) {
      setFormState({
        ...INITIAL_FORM_STATE,
        expandedIds: new Set(),
      });
    }

    previousSectionId.current = currentSectionId;
  }, [selectedSection?.id]);

  return {
    formState,
    setFormState,
    dispatch,
    currentVariantDetails,
    currentUser,
    reduxState,
    componentFieldsConfig,
    parentComponentFieldsConfig,
    treeConfig,
    fieldsConfig,
    fetchMethod,
    deleteMethod,
    parentComponentGetMethod,
    parentComponentCreateMethod,
    parentComponentUpdateMethod,
  };
};

